/**
 * Concept diagram for the undoing course intro.
 *
 * Four stacked tiers, each representing a layer of git's "safety net" — the
 * deeper the layer, the more permanent the save, and the more specialized the
 * undo command. Hovering each tier (visually) reinforces the matching command.
 */
export function SafetyLadder() {
  const tiers = [
    {
      label: "Working tree",
      sub: "Your edits, not saved yet",
      cmd: "git restore <file>",
      hint: "Throws the edit away",
      accent: "#D8443C",
    },
    {
      label: "Staging area",
      sub: "Edits you've added with `git add`",
      cmd: "git restore --staged <file>",
      hint: "Unstages but keeps the edit",
      accent: "#E0A14B",
    },
    {
      label: "Local commits",
      sub: "Saved on your laptop, not pushed",
      cmd: "git reset",
      hint: "Move HEAD backwards (--soft / --mixed / --hard)",
      accent: "#5BC0A2",
    },
    {
      label: "Shared commits",
      sub: "Pushed — others may have pulled",
      cmd: "git revert <commit>",
      hint: "Adds an inverse commit. History is preserved.",
      accent: "#2D9BD6",
    },
    {
      label: "Reflog",
      sub: "Every HEAD move git ever made",
      cmd: "git reflog → git reset --hard HEAD@{N}",
      hint: "Recovers commits even after reset --hard",
      accent: "#A872E0",
    },
  ];

  return (
    <div
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--border-1)",
        borderRadius: 14,
        padding: 22,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          color: "var(--fg-2)",
        }}
      >
        The undo ladder
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {tiers.map((t, i) => (
          <div
            key={t.label}
            style={{
              display: "grid",
              gridTemplateColumns: "44px 1fr auto",
              alignItems: "center",
              gap: 14,
              padding: "12px 14px",
              borderRadius: 10,
              background: "var(--bg-1)",
              border: "1px solid var(--border-1)",
              borderLeft: `4px solid ${t.accent}`,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                background: t.accent,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontFamily: "Montserrat, system-ui, sans-serif",
                fontWeight: 700,
                fontSize: 15,
              }}
            >
              {i + 1}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div
                style={{
                  fontFamily: "Montserrat, system-ui, sans-serif",
                  fontWeight: 600,
                  fontSize: 14,
                  color: "var(--fg-1)",
                }}
              >
                {t.label}
              </div>
              <div style={{ fontSize: 12, color: "var(--fg-2)" }}>{t.sub}</div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                alignItems: "flex-end",
                minWidth: 0,
              }}
            >
              <code
                style={{
                  fontFamily: "ui-monospace, Menlo, monospace",
                  fontSize: 12,
                  color: t.accent,
                  background: "rgba(255,255,255,0.04)",
                  padding: "3px 8px",
                  borderRadius: 6,
                  whiteSpace: "nowrap",
                }}
              >
                {t.cmd}
              </code>
              <div style={{ fontSize: 11, color: "var(--fg-3, var(--fg-2))" }}>
                {t.hint}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 4,
          padding: 12,
          borderRadius: 10,
          background: "rgba(168, 114, 224, 0.08)",
          border: "1px solid rgba(168, 114, 224, 0.25)",
          fontSize: 12,
          color: "var(--fg-2)",
          lineHeight: 1.5,
        }}
      >
        <b style={{ color: "var(--fg-1)" }}>Rule of thumb:</b> match the undo
        to the layer the mistake landed in. The further down the ladder, the
        more careful you have to be — and the more git's safety net matters.
      </div>
    </div>
  );
}
